import type { RoomLayout, Questionnaire } from './types.ts';

/**
 * Transforms the Room Layout and user Questionnaire into a clean prompt context
 * suitable for feeding into the LLM (Gemini).
 * 
 * Provides explicit instructions on the intuitive coordinate system (Z as height/elevation).
 * 
 * @param roomDb The room layout representation.
 * @param questionnaire Questionnaire feedback from the user.
 * @returns An object containing text instructions, context description, and current coordinates.
 */
export function transformToLLMContext(
  roomDb: RoomLayout,
  questionnaire: Questionnaire
): { promptContext: string; currentLayoutJson: string } {
  const promptContext = `
Room Dimensions:
- Width (X): ${roomDb.width} meters (horizontal range: [0, ${roomDb.width}])
- Length (Y): ${roomDb.length} meters (horizontal range: [0, ${roomDb.length}])
- Height (Z): ${roomDb.height} meters (vertical range: [0, ${roomDb.height}])

Coordinate System Rules:
1. Corner Origin: The bottom-left corner of the room floor is at (0, 0, 0).
2. Axes:
   - X: width of the room (left-to-right)
   - Y: length of the room (front-to-back)
   - Z: elevation height above the floor (0.0 means sitting on the floor)
3. Sizes:
   - size.width: item width (along X axis)
   - size.length: item length/depth (along Y axis)
   - size.height: item height (along Z axis)
4. Rotation: Rotation in degrees (0 to 360) around the vertical Z-axis.

Occupants/Preferences:
- Adults: ${questionnaire.numberOfAdults}
- Children: ${questionnaire.numberOfChildren}
- Guests: ${questionnaire.numberOfGuests}
- Needs Workplace: ${questionnaire.workplace ? 'Yes' : 'No'}
- Needs Playground: ${questionnaire.playground ? 'Yes' : 'No'}
`;

  // Format a simplified representation of the items for clean context
  const itemsJson = roomDb.items.map(item => ({
    itemId: item.itemId,
    label: item.label,
    description: item.description,
    type: item.type,
    size: {
      width: Math.round(item.size.width * 1000) / 1000,
      length: Math.round(item.size.length * 1000) / 1000,
      height: Math.round(item.size.height * 1000) / 1000
    },
    position: {
      x: Math.round(item.position.x * 1000) / 1000,
      y: Math.round(item.position.y * 1000) / 1000,
      z: Math.round(item.position.z * 1000) / 1000
    },
    rotationDegrees: Math.round(item.angle),
    materials: item.materials || []
  }));

  const currentLayoutJson = JSON.stringify(itemsJson, null, 2);

  return {
    promptContext,
    currentLayoutJson,
  };
}
